/**
 * useDisplayCut - when the display plays a stage moment (plan map-display, I).
 *
 * A moment is NEW when its stamp changes once this screen has settled: a reload or a late join shows the stage
 * as it stands and replays nothing (compared by value, not by clock - the GM's clock wrote the stamp). The cut's
 * rise waits for two things a player's screen hears separately: the new set (the terrain arrives on its own
 * subscription, before or after the stage) and the curtain lifting (a NEW SCENE cuts with it down, and a rise
 * nobody can see is no rise). Whichever comes last releases it; if the set never changes (the same map) it goes
 * after a short wait.
 *
 * @param {{ cutAt: number|null, struckAt: number|null, solids: unknown, curtained: boolean }} args
 * @returns {{ rise: number, struck: number|null }} `rise` counts the rises played (0: none yet); `struck` is a
 *   strike since mount, or null
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a cut waits for its set before it rises on what is there. */
const SET_WAIT_MS = 2000;
/** How long after mount what the screen hears is where it came in (the stage's first snapshot), not news. */
const SETTLE_MS = 1500;

/**
 * The stamp, once it changes after the screen has settled; null before. What it hears in its first moments - the
 * stage arriving after the map mounted, on a reload or a late join - is where it came in, not a cut.
 */
function useLater(at) {
  const mountedAt = useRef(Date.now());
  const first = useRef(at);
  if (Date.now() - mountedAt.current < SETTLE_MS) first.current = at;
  return at && at !== first.current ? at : null;
}

export default function useDisplayCut({ cutAt, struckAt, solids, curtained }) {
  const cut = useLater(cutAt);
  const struck = useLater(struckAt);
  const [rise, setRise] = useState(0);
  const pending = useRef(null);
  const setAt = useRef(0);
  const shown = useRef(solids);
  const hidden = useRef(curtained);
  hidden.current = curtained;
  const release = useCallback(() => {
    if (!pending.current?.setReady || hidden.current) return;
    pending.current = null;
    setRise((n) => n + 1);
  }, []);
  useEffect(() => {
    if (shown.current === solids) return;
    shown.current = solids;
    setAt.current = Date.now();
    if (pending.current) pending.current.setReady = true;
    release();
  }, [solids, release]);
  useEffect(() => {
    if (!cut) return undefined;
    // The set may already have come (the terrain beat the stage here).
    pending.current = { setReady: Date.now() - setAt.current < SET_WAIT_MS };
    release();
    const t = setTimeout(() => {
      if (pending.current) pending.current.setReady = true;
      release();
    }, SET_WAIT_MS);
    return () => clearTimeout(t);
  }, [cut, release]);
  useEffect(() => {
    if (!curtained) release();
  }, [curtained, release]);
  return { rise, struck };
}
